<template>
  <div id="app" :class="isMobile ? 'isMobile' : ''">
    <keep-alive :key="token">
      <router-view />
    </keep-alive>

    <!-- PWA 更新提示 -->
    <div v-if="showUpdate" class="update-toast">
      <span>发现探数蚁新版本，是否立即更新？</span>
      <button @click="reloadPage">立即更新</button>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { isMobile } from '@/utils/deviceDetector';
import { getBaseURL } from "@/utils/request";
import { ScreenOrientation } from '@capacitor/screen-orientation';
import store from "@/store";

const baseURL = getBaseURL();

export default {
  provide() {
    return {
      reload: this.reload,
    };
  },
  data() {
    return {
      isRouterAlive: true,
      refreshTimer: null,
      hasUserLeft: false,
      beforeunloadHandler: null,
      deviceHeight: 0,
      currentOrientation: '未知',
      showUpdate: false,          // 控制更新提示
    };
  },
  computed: {
    ...mapState(["messageStorage", "isMobile", "token"]),
    key() {
      return this.$route.path + Math.random();
    },
  },
  created() {
    store.commit("getTheme");
  },
  async mounted() {
    // ---------- 原有挂载逻辑 ----------
    store.commit("SET_DEVICE_TYPE", isMobile());
    this.deviceHeight = window.innerHeight;
    this.debouncedHandleResize = this.debounce(this.handleResize, 200);
    window.addEventListener('resize', this.debouncedHandleResize);
    this.debouncedHandleResize();

    try {
      await this.getCurrentOrientation();
      console.log('初始方向获取成功');
    } catch (err) {
      console.error('初始方向获取失败:', err);
      this.currentOrientation = '获取失败，请重试';
    }

    this.orientationListener = ScreenOrientation.addListener('screenOrientationChange', (event) => {
      this.currentOrientation = event.type;
      console.log('方向变化:', event.type);
    });

    // ✅ 监听 register-service-worker 发出的更新事件
    window.addEventListener('sw-updated', this.showUpdateToast);
  },
  beforeDestroy() {
    if (this.orientationListener) {
      this.orientationListener.remove();
    }
    window.removeEventListener('resize', this.handleResize);
    if (this.beforeunloadHandler) {
      window.removeEventListener("beforeunload", this.beforeunloadHandler);
    }
    window.removeEventListener('sw-updated', this.showUpdateToast);
  },
  methods: {
    // ---------- 原有方法 ----------
    debounce(fn, delay = 600) {
      let timer = null;
      return (...args) => {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), delay);
      };
    },
    async lockPortrait() {
      try {
        await ScreenOrientation.lock({ type: 'portrait' });
        alert('已锁定为竖屏');
      } catch (err) {
        console.error('锁定失败:', err);
        alert('无法锁定方向，请检查权限');
      }
    },
    async lockLandscape() {
      try {
        await ScreenOrientation.lock({ type: 'landscape' });
        alert('已锁定为横屏');
      } catch (err) {
        console.error('锁定失败:', err);
      }
    },
    async unlock() {
      try {
        await ScreenOrientation.unlock();
        alert('已解除锁定');
      } catch (err) {
        console.error('解除锁定失败:', err);
      }
    },
    async getCurrentOrientation() {
      try {
        if (ScreenOrientation.getCurrentOrientation) {
          const result = await ScreenOrientation.getCurrentOrientation();
          this.currentOrientation = result.type;
        }
      } catch (err) {
        console.error('获取方向失败:', err);
        throw err;
      }
    },
    async handleResize() {
      document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
      let viewportWidth = document.documentElement.clientWidth;
      const currentIsMobile = isMobile();
      const flag = (currentIsMobile || viewportWidth <= 1023);
      store.commit("SET_DEVICE_TYPE", flag);
      if (flag) {
        viewportWidth = Math.min(viewportWidth, 750);
        let fontSize = (viewportWidth / 750) * 100;
        document.documentElement.style.fontSize = fontSize + 'px';
        await this.$nextTick();
        store.commit("SET_FOLDROBOT");
        store.commit("SET_FOLDHISTORY");
      } else {
        document.documentElement.style.fontSize = "";
        document.body.style.transform = 'scale(1)';
      }
    },
    addBeforeunloadListener() {
      this.beforeunloadHandler = (event) => {
        event.preventDefault();
        let sessionIds = [];
        for (const robotId in this.messageStorage) {
          sessionIds = [
            ...Object.keys(this.messageStorage[robotId]),
            ...sessionIds,
          ];
        }
        window.navigator.sendBeacon(
          `${baseURL}/DelSession`,
          JSON.stringify({ sessionId: sessionIds })
        );
      };
      window.addEventListener("beforeunload", this.beforeunloadHandler);
    },
    async reload() {
      this.isRouterAlive = false;
      await this.$nextTick();
      this.isRouterAlive = true;
    },

    // ---------- ✅ PWA 更新方法 ----------
    showUpdateToast() {
      this.showUpdate = true;
    },

    reloadPage() {
      this.showUpdate = false;
      const url = new URL(window.location.href);
      // 清除可能存在的旧 _t 参数，保证每次都是新的
      url.searchParams.delete('_t');
      url.searchParams.set('_t', Date.now());
      window.location.replace(url.toString());
    },
  },
};
</script>

<style lang="scss">
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  height: 100%;
}

:root {
  --app-height: 100vh;
}

.update-toast {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  background: #333;
  color: #fff;
  padding: 12px 24px;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  gap: 16px;
  z-index: 9999;
  font-size: 14px;
  font-family: Avenir, Helvetica, Arial, sans-serif;
  white-space: nowrap;

  button {
    background: #4caf50;
    border: none;
    color: #fff;
    padding: 6px 18px;
    border-radius: 4px;
    cursor: pointer;
    font-weight: bold;
    font-size: 14px;
    transition: background 0.2s;

    &:hover {
      background: #45a049;
    }
  }
}

.isMobile .update-toast {
  bottom: 16px;
  padding: 10px 16px;
  font-size: 13px;
  white-space: normal;
  max-width: 90%;
}
</style>